import { timestampBase36 } from '@common/utils/id.helpers';

export const AUTH_SNAPSHOT_STORAGE_KEY = 'authStore';
export const SESSION_TRANSITION_STORAGE_KEY = '_EP-SESSION-TRANSITION_';
export const LEGACY_CACHE_EPOCH = 'legacy';
export const STORAGE_INFO_BASE_KEY = '_EP-STORES_';

const STALE_TRANSITION_MS = 30000;

export type SessionTransition = {
  id: string;
  fromEpoch: string;
  toEpoch: string;
  startedAt: number;
};

let runtimeCacheEpoch: string = null;
let runtimeCacheEpochPromise: Promise<string> = null;

const parseJson = <T>(raw: string | null): T | null => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch (_) {
    return null;
  }
};

export const readSessionTransition = (): SessionTransition | null =>
  parseJson<SessionTransition>(localStorage.getItem(SESSION_TRANSITION_STORAGE_KEY));

export const readPersistedCacheEpoch = (): string => {
  const snapshot = parseJson<{ auth?: { cacheEpoch?: string } }>(localStorage.getItem(AUTH_SNAPSHOT_STORAGE_KEY));
  return snapshot?.auth?.cacheEpoch || LEGACY_CACHE_EPOCH;
};

/**
 * Impedisce al bootstrap di catturare auth/epoch mentre un'altra tab sta eliminando la cache.
 * Un marker orfano non puo' bloccare l'app per sempre: allo scadere viene rimosso e la normale
 * pulizia key-only del bootstrap completa l'eventuale transizione interrotta.
 */
export const waitForSessionTransition = (): Promise<void> => {
  const transition = readSessionTransition();
  if (!transition) {
    return Promise.resolve();
  }

  if (Date.now() - transition.startedAt >= STALE_TRANSITION_MS) {
    localStorage.removeItem(SESSION_TRANSITION_STORAGE_KEY);
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const finish = () => {
      window.removeEventListener('storage', onStorage);
      clearTimeout(timeout);
      resolve();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === SESSION_TRANSITION_STORAGE_KEY && !event.newValue) {
        finish();
      }
    };
    window.addEventListener('storage', onStorage);
    const timeout = setTimeout(() => {
      const current = readSessionTransition();
      if (current?.id === transition.id) {
        localStorage.removeItem(SESSION_TRANSITION_STORAGE_KEY);
      }
      finish();
    }, Math.max(0, STALE_TRANSITION_MS - (Date.now() - transition.startedAt)));
  });
};

/** L'epoch e' catturata una sola volta per runtime, ma soltanto dopo l'eventuale transizione. */
export const initializeRuntimeCacheEpoch = (): Promise<string> => {
  if (runtimeCacheEpoch) {
    return Promise.resolve(runtimeCacheEpoch);
  }
  if (!runtimeCacheEpochPromise) {
    runtimeCacheEpochPromise = waitForSessionTransition().then(() => {
      runtimeCacheEpoch = readPersistedCacheEpoch();
      return runtimeCacheEpoch;
    });
  }
  return runtimeCacheEpochPromise;
};

export const getRuntimeCacheEpoch = (): string => runtimeCacheEpoch || readPersistedCacheEpoch();

export const createCacheEpoch = (): string => timestampBase36();

/**
 * Invalida la sessione persistita assegnandole anche una nuova epoch. Scrivere uno snapshot senza
 * token, invece di rimuovere soltanto la chiave, impedisce ai vecchi runtime ancora su `legacy` di
 * superare il fencing e riscrivere l'auth prima di ricevere lo storage event e ricaricare.
 */
export const invalidatePersistedSession = (): void => {
  const snapshot = parseJson<{ auth?: { [key: string]: any }; $cache?: { [key: string]: any } }>(
    localStorage.getItem(AUTH_SNAPSHOT_STORAGE_KEY)
  );

  if (!snapshot?.auth) {
    localStorage.removeItem(AUTH_SNAPSHOT_STORAGE_KEY);
    localStorage.removeItem(SESSION_TRANSITION_STORAGE_KEY);
    return;
  }

  const auth = {
    ...snapshot.auth,
    accessToken: null,
    refreshToken: null,
    userId: null,
    user: null,
    projectRole: null,
    cacheEpoch: createCacheEpoch(),
  };
  localStorage.setItem(AUTH_SNAPSHOT_STORAGE_KEY, JSON.stringify({
    ...snapshot,
    auth,
    $cache: { ...(snapshot.$cache || {}), auth: false },
  }));
  localStorage.removeItem(SESSION_TRANSITION_STORAGE_KEY);
};

export const beginSessionTransition = (toEpoch: string): SessionTransition => {
  const transition: SessionTransition = {
    id: createCacheEpoch(),
    fromEpoch: getRuntimeCacheEpoch(),
    toEpoch,
    startedAt: Date.now(),
  };
  localStorage.setItem(SESSION_TRANSITION_STORAGE_KEY, JSON.stringify(transition));
  return transition;
};

export const completeSessionTransition = (transitionId: string): void => {
  const current = readSessionTransition();
  if (!current || current.id === transitionId) {
    localStorage.removeItem(SESSION_TRANSITION_STORAGE_KEY);
  }
};

/**
 * Fencing cross-tab: dopo il marker o il cambio epoch un vecchio runtime non puo' piu' persistere.
 */
export const canRuntimePersist = (): boolean =>
  !readSessionTransition() && getRuntimeCacheEpoch() === readPersistedCacheEpoch();

export const storageInfoKeyForEpoch = (epoch: string): string =>
  epoch === LEGACY_CACHE_EPOCH ? STORAGE_INFO_BASE_KEY : `${STORAGE_INFO_BASE_KEY}::${epoch}`;

const storageInfoKeys = (): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key && (key === STORAGE_INFO_BASE_KEY || key.startsWith(`${STORAGE_INFO_BASE_KEY}::`))) {
      keys.push(key);
    }
  }
  return keys;
};

export const clearStorageInfoKeys = (): void => {
  storageInfoKeys().forEach((key) => localStorage.removeItem(key));
};

export const purgeObsoleteStorageInfoKeys = (activeEpoch: string): void => {
  const activeKey = storageInfoKeyForEpoch(activeEpoch);
  const obsoleteKeys = storageInfoKeys().filter((key) => key !== activeKey);
  obsoleteKeys.forEach((key) => localStorage.removeItem(key));
};

export const currentStorageInfoKey = (): string => {
  const epoch = getRuntimeCacheEpoch();
  purgeObsoleteStorageInfoKeys(epoch);
  return storageInfoKeyForEpoch(epoch);
};
